import React, { useEffect, useMemo, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import { useFetchCustomTiles } from '../../hooks/toolbox-hooks';
import { TileDefinition } from '../../types/dashboard';
import { TileRuntimeContext } from '../../support/tileRuntimeContext';
import { normalizeLeaderTileSettings } from '../../support/tileDisplayContextUtils';
import LeaderTile from './LeaderTile';

interface Props {
    tileNames?: string[];
    tileIds?: number[];
    runtimeContext?: TileRuntimeContext;
    className?: string;
    enableDebugLogging?: boolean;
    setDateRange?: (dateRange: string) => void;
    /** Page-level `current_time_filter` (`all` / `range:…`) applied to every tile, overriding per-tile state. */
    currentTimeFilter?: string;
    /** Maps a tile's configured name to the title shown (e.g. to swap a hard-coded "Current FY"). */
    titleTransform?: (name: string) => string;
}

function normalizeName(s: string) {
    return String(s ?? '').trim().toLowerCase();
}

export default function LeaderTileStrip({
  tileNames = [],
  tileIds = [],
  runtimeContext = {},
  className,
  enableDebugLogging = false,
  setDateRange,
  currentTimeFilter,
  titleTransform,
}: Props) {
  const { data: customTiles, isLoading, error } = useFetchCustomTiles();
  const [tileErrors, setTileErrors] = useState<Record<number, string | null>>({});
  const [resizeTick, setResizeTick] = useState(0);

  void setDateRange;

  const tiles: TileDefinition[] = useMemo(() => {
    const arr = Array.isArray(customTiles) ? customTiles : [];
    const byId = new Map<number, any>(arr.map((t: any) => [Number(t.id), t]));
    const byName = new Map<string, any>(arr.map((t: any) => [normalizeName(t.name), t]));

    const resolved: any[] = [];

    for (const n of tileNames) {
      const t = byName.get(normalizeName(n));
      if (t) resolved.push(t);
    }

    for (const id of tileIds) {
      const t = byId.get(Number(id));
      if (t) resolved.push(t);
    }

    const seen = new Set<number>();
    const deduped = resolved.filter((t) => {
      const id = Number(t.id);
      if (!Number.isFinite(id)) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const processed = deduped.map((t: any) => {
      const baseTile = { ...t, id: Number(t.id) };
      const normalized = normalizeLeaderTileSettings(baseTile);

      if (enableDebugLogging) {
        console.log(`LeaderTileStrip: Tile ${t.name}`, {
          original: t.settings,
          normalized: normalized.settings,
          margins: {
            top: normalized.settings?.marginTop,
            right: normalized.settings?.marginRight,
            bottom: normalized.settings?.marginBottom,
            left: normalized.settings?.marginLeft,
          },
          hasCustomMargins: 
            (t.settings?.marginLeft !== undefined && t.settings?.marginLeft !== 16) ||
            (t.settings?.marginRight !== undefined && t.settings?.marginRight !== 16) ||
            (t.settings?.marginTop !== undefined && t.settings?.marginTop !== 24) ||
            (t.settings?.marginBottom !== undefined && t.settings?.marginBottom !== 48) ||
            t.settings?.baseMargins !== undefined ||
            t.settings?.legendMargins !== undefined
        });
      }
      return normalized;
    });

    return processed;
  }, [customTiles, tileNames, tileIds, enableDebugLogging]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setResizeTick((t) => t + 1);
      }, 100);
    };

    window.addEventListener('resize', onResize);
    const initialTimeout = setTimeout(() => setResizeTick((t) => t + 1), 50);

    return () => {
      clearTimeout(timeout);
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', onResize);
    };
  }, [tiles.length]);

  if (isLoading) return <HbrSpinner>Loading tile definitions from the Toolbox database…</HbrSpinner>;
  if (error) return <div style={{ color: 'red' }}>{(error as Error).message}</div>;
  if (!tiles.length) return null;

    return (
      <div
        className={`grid gap-3 w-full h-full min-h-0 ${className ?? ''}`}
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
          overflow: 'visible',
          position: 'relative',
          zIndex: 0,
        }}
      >
        {tiles.map((tile) => {
          void resizeTick;

          const err = tileErrors[tile.id] ?? null;

          return (
            <LeaderTile
              key={tile.id}
              tile={tile}
              runtimeContext={runtimeContext}
              setTileErrors={setTileErrors}
              err={err}
              currentTimeFilterOverride={currentTimeFilter as any}
              titleOverride={titleTransform ? titleTransform(tile.name) : undefined}
            />
          );
        })}
      </div>
    );
}